import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { AuctionCard } from '../components/AuctionCard';
import { Button } from '../components/ui/button';
import { ArrowRight, Gavel, TrendingUp, Shield, Zap } from 'lucide-react';
import api from '../utils/api';

export const Landing = () => {
  const navigate = useNavigate();
  const [featuredAuctions, setFeaturedAuctions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAuctions();
  }, []);

  const loadAuctions = async () => {
    try {
      const response = await api.get('/auctions?status=active');
      setFeaturedAuctions(response.data.slice(0, 8));
    } catch (error) {
      console.error('Failed to load auctions:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="py-16 md:py-24 lg:py-32">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-bold tracking-tighter leading-none font-heading mb-6" data-testid="hero-heading">
              Precision Bidding.
              <br />
              <span className="text-muted-foreground">Maximum Value.</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 max-w-2xl mx-auto">
              The modern auction platform for collectors, traders, and enthusiasts. Real-time bidding with transparent pricing.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/browse')}
                className="h-12 px-8 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-medium text-base transition-all active:scale-95"
                data-testid="browse-auctions-button"
              >
                Browse Auctions
                <ArrowRight className="ml-2 h-5 w-5" strokeWidth={1.5} />
              </Button>
              <Button
                onClick={() => navigate('/signup')}
                variant="outline"
                className="h-12 px-8 rounded-sm font-medium text-base border-2"
                data-testid="start-bidding-button"
              >
                Start Bidding
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24 bg-secondary/30">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-primary text-primary-foreground mb-4">
                <Zap className="h-7 w-7" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold font-heading mb-2">Real-Time Bidding</h3>
              <p className="text-muted-foreground">Live updates on all auctions. Never miss a winning opportunity.</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-primary text-primary-foreground mb-4">
                <Shield className="h-7 w-7" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold font-heading mb-2">Secure Payments</h3>
              <p className="text-muted-foreground">Stripe-powered checkout for safe and fast transactions.</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-primary text-primary-foreground mb-4">
                <TrendingUp className="h-7 w-7" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold font-heading mb-2">Buy It Now</h3>
              <p className="text-muted-foreground">Skip the wait. Purchase items instantly at fixed prices.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Auctions */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight font-heading mb-2">Live Auctions</h2>
              <p className="text-muted-foreground">Bid now on exclusive items</p>
            </div>
            <Button
              onClick={() => navigate('/browse')}
              variant="ghost"
              className="rounded-sm"
              data-testid="view-all-button"
            >
              View All
              <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12 text-muted-foreground font-mono">Loading auctions...</div>
          ) : featuredAuctions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">No active auctions at the moment.</p>
              <Button onClick={() => navigate('/create')} data-testid="create-first-auction-button">
                Create the First Auction
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredAuctions.map((auction) => (
                <AuctionCard key={auction.auction_id} auction={auction} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center">
          <Gavel className="h-12 w-12 mx-auto mb-6" strokeWidth={1.5} />
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight font-heading mb-4">
            Ready to Start?</h2>
          <p className="text-lg text-primary-foreground/80 mb-8 max-w-xl mx-auto">
            Join thousands of buyers and sellers in the most transparent auction platform.
          </p>
          <Button
            onClick={() => navigate('/signup')}
            className="h-12 px-8 rounded-sm bg-white text-primary hover:bg-white/90 font-medium text-base"
            data-testid="cta-signup-button"
          >
            Create Free Account
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center text-sm text-muted-foreground">
          <p>© 2026 Green Frog Auctions. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};
